import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
} from 'react';
import { useEnterKeyNavigation } from '@/hooks/useEnterKeyNavigation';
import { useShiftTabReset } from '@/hooks/useShiftTabReset';
import { useIosCaretFix } from '@/hooks/useIosCaretFix';
import useAppToast from '@/hooks/useAppToast';
import useAppModal from '@/hooks/useAppModal';
import {
  NETWORK_ERROR_EVENT,
  NOT_FOUND_ERROR_EVENT,
  SERVER_ERROR_EVENT,
} from '@/shared/lib/react-query';
import { tenantCapability } from '@customize/registry';
import AppVersionWatcher from './AppVersionWatcher';

export const FORCE_LOGOUT_EVENT = 'app-force-logout';

/** The site the current path belongs to — its first segment. */
const sitePathSegment = (router?: GlobalBehaviorRouter): string | undefined => {
  const pathname =
    router?.state.location.pathname ??
    (typeof window !== 'undefined' ? window.location.pathname : '');

  return pathname.split('/')[1] || undefined;
};

interface GlobalBehaviorRouter {
  state: {
    location: {
      pathname: string;
      search: string;
      hash: string;
    };
  };
  subscribe: (listener: (state: GlobalBehaviorRouter['state']) => void) => () => void;
}

// Dedupe simultaneous 5xx errors so only one modal opens per error burst.
const SERVER_ERROR_DEDUPE_MS = 1000;
let serverErrorDispatched = false;
// The browser's `offline` event and the failed request both report the same loss,
// so they share one key: the second replaces the first instead of stacking under it.
const NETWORK_ERROR_TOAST_KEY = 'network-error';

interface FormChangeState {
  hasChanges: boolean;
  message?: string;
}

/**
 * Context for managing global keyboard navigation behaviors and form change warnings
 */
interface GlobalBehaviorContextType {
  enterKeyNavigationEnabled: boolean;
  shiftTabResetEnabled: boolean;
  setEnterKeyNavigationEnabled: (enabled: boolean) => void;
  setShiftTabResetEnabled: (enabled: boolean) => void;
  formChangeState: FormChangeState;
  registerFormChange: (hasChanges: boolean, message?: string) => void;
  clearFormChange: () => void;
}

const GlobalBehaviorContext = createContext<GlobalBehaviorContextType | undefined>(undefined);

/**
 * Hook to access and control global behavior settings and form changes
 */
export const useGlobalBehavior = () => {
  const context = useContext(GlobalBehaviorContext);
  if (!context) {
    throw new Error('useGlobalBehavior must be used within GlobalBehaviorProvider');
  }
  return context;
};

const GlobalBehaviorProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [enterKeyNavigationEnabled, setEnterKeyNavigationEnabled] = useState(true);
  const [shiftTabResetEnabled, setShiftTabResetEnabled] = useState(false);
  const [formChangeState, setFormChangeState] = useState<FormChangeState>({
    hasChanges: false,
  });

  const registerFormChange = useCallback((hasChanges: boolean, message?: string) => {
    setFormChangeState({ hasChanges, message });
  }, []);

  const clearFormChange = useCallback(() => {
    setFormChangeState({ hasChanges: false });
  }, []);

  // Warn on tab close / refresh when form has unsaved changes
  useEffect(() => {
    if (!formChangeState.hasChanges) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue =
        formChangeState.message || '保存されていない変更があります。本当にこのページを離れますか？';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [formChangeState]);

  return (
    <GlobalBehaviorContext.Provider
      value={{
        enterKeyNavigationEnabled,
        shiftTabResetEnabled,
        setEnterKeyNavigationEnabled,
        setShiftTabResetEnabled,
        formChangeState,
        registerFormChange,
        clearFormChange,
      }}
    >
      {children}
    </GlobalBehaviorContext.Provider>
  );
};

/**
 * Global Behavior Wrapper
 *
 * Enables global keyboard navigation, form change warnings, and other app-wide behaviors.
 * Wraps the main router provider to ensure global hooks are active.
 *
 * Keyboard behaviors are controlled via useGlobalBehavior():
 * - setEnterKeyNavigationEnabled(false) — disable Enter key navigation
 * - setShiftTabResetEnabled(false) — disable Shift+Tab reset
 *
 * Form change tracking is handled by useFormChangeGuard():
 * @see {@link @/hooks/useFormChangeGuard}
 *
 * @example
 * const { setEnterKeyNavigationEnabled } = useGlobalBehavior();
 * setEnterKeyNavigationEnabled(false);
 *
 * @example
 * const { hasFormChanged, confirmIfChanged, clearFormChange } = useFormChangeGuard(formData, initialData);
 * const handleClose = () => confirmIfChanged(() => { clearFormChange(); onClose(); });
 */
const GlobalBehaviorContent: React.FC<{
  children: React.ReactNode;
  router?: GlobalBehaviorRouter;
}> = ({ children, router }) => {
  useEnterKeyNavigation();
  useShiftTabReset();
  useIosCaretFix();

  const [networkOffline, setNetworkOffline] = useState(
    typeof window !== 'undefined' ? !window.navigator.onLine : false
  );
  /*
   * Whether a lost connection covers the app with a full-page notice. A site
   * may turn it off (`networkErrorOverlay` in its customization) and report
   * the loss by toast alone, keeping the screen and its input where they are.
   * The site is the path's first segment, as `App`'s route branding reads it.
   */
  const [networkOverlay, setNetworkOverlay] = useState(() =>
    tenantCapability(sitePathSegment(router), 'networkErrorOverlay', true)
  );
  const toast = useAppToast();
  const appModal = useAppModal();
  const { formChangeState } = useGlobalBehavior();

  // Follow the site as the operator moves between portals.
  useEffect(() => {
    if (!router) return undefined;

    return router.subscribe(state =>
      setNetworkOverlay(
        tenantCapability(state.location.pathname.split('/')[1], 'networkErrorOverlay', true)
      )
    );
  }, [router]);

  useEffect(() => {
    const handleOffline = () => {
      setNetworkOffline(true);
      // With no page drawn over the screen the operator would learn of the
      // loss only on their next request, so the toast comes now instead.
      if (!networkOverlay) {
        toast.error('インターネット接続エラーが発生しました。', {
          bypassSuppression: true,
          key: NETWORK_ERROR_TOAST_KEY,
        });
      }
    };
    const handleOnline = () => setNetworkOffline(false);

    const handleForceLogout = (event: Event) => {
      const customEvent = event as CustomEvent<{ message: string }>;
      const message = customEvent.detail?.message;
      if (!message) return;
      toast.error(message, { bypassSuppression: true });
    };

    const handleNetworkError = (event: Event) => {
      const customEvent = event as CustomEvent<{ message: string }>;
      const isBrowserOffline = typeof window !== 'undefined' && !window.navigator.onLine;
      setNetworkOffline(isBrowserOffline);
      toast.error(customEvent.detail?.message || 'インターネット接続エラーが発生しました。', {
        bypassSuppression: true,
        key: NETWORK_ERROR_TOAST_KEY,
      });
    };

    const handleNotFoundError = (event: Event) => {
      const customEvent = event as CustomEvent<{ message: string }>;
      toast.error(customEvent.detail?.message || 'データが見つかりません。');
    };

    const handleServerError = (event: Event) => {
      if (serverErrorDispatched) return;
      serverErrorDispatched = true;
      setTimeout(() => {
        serverErrorDispatched = false;
      }, SERVER_ERROR_DEDUPE_MS);
      const customEvent = event as CustomEvent<{ message: string }>;
      appModal.warning({
        content:
          customEvent.detail?.message ||
          'エラーが発生しました。しばらくしてからもう一度お試しください。',
        okText: '閉じる',
      });
    };

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);
    window.addEventListener(NETWORK_ERROR_EVENT, handleNetworkError);
    window.addEventListener(NOT_FOUND_ERROR_EVENT, handleNotFoundError);
    window.addEventListener(FORCE_LOGOUT_EVENT, handleForceLogout);
    window.addEventListener(SERVER_ERROR_EVENT, handleServerError);
    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener(NETWORK_ERROR_EVENT, handleNetworkError);
      window.removeEventListener(NOT_FOUND_ERROR_EVENT, handleNotFoundError);
      window.removeEventListener(FORCE_LOGOUT_EVENT, handleForceLogout);
      window.removeEventListener(SERVER_ERROR_EVENT, handleServerError);
    };
  }, [toast, appModal, networkOverlay]);

  return (
    <>
      <AppVersionWatcher hasFormChanges={formChangeState.hasChanges} router={router} />
      {children}
      {networkOffline && networkOverlay && (
        <div
          role="alert"
          aria-live="assertive"
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-white"
        >
          <div className="max-w-md px-6 text-center">
            <h1 className="mb-3 text-xl font-bold text-gray-900">
              インターネット接続エラーが発生しました。
            </h1>
            <p className="text-sm text-gray-700">
              ネットワーク接続を確認してから、もう一度お試しください。
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export const GlobalBehaviorWrapper: React.FC<{
  children: React.ReactNode;
  router?: GlobalBehaviorRouter;
}> = ({ children, router }) => {
  return (
    <GlobalBehaviorProvider>
      <GlobalBehaviorContent router={router}>{children}</GlobalBehaviorContent>
    </GlobalBehaviorProvider>
  );
};
